import React, { ForwardRefExoticComponent, SVGProps } from 'react'

import { Check } from '@factorialco/f0-react/icons/app'
import { FormsElementTypeEnum } from 'generated/resources/datatable.graphql'

import i18n from 'modules/core/lib/i18n'

import { OptionElementQuestion } from 'modules/forms/components/QuestionComponent/OptionElementQuestion'
import ChoiceElementEditor from 'modules/forms/components/editor/ChoiceElementEditor/ChoiceElementEditor'
import { Element } from 'modules/forms/lib/Element'
import { Answer, Options, elementParam } from 'modules/forms/lib/types'

/**
 * MultipleChoiceElement class for elements with multiple selectable options.
 * Extends the base Element class.
 */
export class MultipleChoiceElement extends Element {
  static create({
    label,
    options = {},
  }: {
    label: string
    options: Options
  }): MultipleChoiceElement {
    const el = new MultipleChoiceElement({
      label,
      description: options.description,
      mandatory: options.mandatory || options.locked, //locked element are always mandatory
    })
    if (options.locked) {
      el.lock()
    }

    return el
  }
  /**
   * Get the name of the element
   */
  getName(): string {
    return i18n.t('forms.modals.elements.multiple_choice')
  }

  /**
   * Get the type of the element
   */
  getType(): FormsElementTypeEnum {
    return 'multiselect'
  }

  /**
   * Get the icon for the element
   */
  getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>> {
    return Check
  }

  /**
   * Validate the element's configuration
   */
  validate(validateCorrectOption: boolean): Record<string, string> {
    const errors = super.validate(validateCorrectOption)

    const options = this.options
    if (options.length < 2) {
      // At least two options are required
      errors.options = i18n.t('forms.editor.validation.min_two_options')
    } else if (options.some((option) => !option.label.trim())) {
      //All options require a title
      errors.options = i18n.t('forms.editor.validation.option_label_required')
    } else if (validateCorrectOption && !options.some((option) => option.isCorrect)) {
      // At least one correct answer is required
      errors.options = i18n.t('forms.editor.validation.no_correct_option')
    }

    return errors
  }

  /**
   * Get a React component for editing this element
   *
   * @param _onChange - Callback function to handle changes to the element (unused)

   * @returns A React component for editing this element
   */
  getEditorComponent(
    onChange: (element: Element) => void,
    enableCorrectConfiguration: boolean
  ): React.ReactNode {
    return (
      <ChoiceElementEditor
        element={this}
        onChange={onChange}
        enableCorrectConfiguration={enableCorrectConfiguration}
      />
    )
  }

  getPreviewComponent(disabled?: boolean, showCorrectAnswerIcons?: boolean): React.ReactNode {
    return (
      <OptionElementQuestion
        element={this}
        isSingleChoice={false}
        disabled={disabled}
        showCorrectAnswerIcons={showCorrectAnswerIcons}
      />
    )
  }

  getQuestionComponent(onChange: (answer: Answer) => void): React.ReactNode {
    return (
      <OptionElementQuestion
        element={this}
        isSingleChoice={false}
        onChange={onChange}
        showCorrectAnswerIcons={false}
      />
    )
  }

  configureParams(): elementParam[] {
    return [Element.booleanParam('randomizeOptions', 'Randomize')]
  }
}
